import { Container, DisplayObject } from 'pixi.js'
import { gameConfig } from '../../../config'
import gsap from 'gsap'
import { changeNode } from '../../game/util/gameUtil'
import { useGameStore } from '../../game/store/GameStore'
import { SymbolType } from '../../game/enum/SymbolType'
import { symbolUtil } from '../../spineManager'
import { playStopSound } from '../../soundManager/util/soundUtil'
import { ga4Info } from '@slot-nx/core'
import { SlotSymbolInfo } from '../../placeOrder/api/PlaceOrderResponse'

interface ISpinService {
  spin(): void
}

export class SpinService implements ISpinService {
  public reelNodes: Container[]

  public reelNodeRef: Container
  public reelDisplayNodeRef: Container
  public currentSpinTimeline: gsap.core.Timeline | null = null
  private immediatelyStopRemainingReels: number[] = []
  private showLoagingId: string | number | NodeJS.Timeout | undefined

  public async initReels(reels: Container[], reelNodeRef: Container) {
    this.reelNodes = reels
    this.reelNodeRef = reelNodeRef
  }

  public async initDisplayReels(reelDisplayNodeRef: Container) {
    this.reelDisplayNodeRef = reelDisplayNodeRef
  }

  public emergencyStop() {
    useGameStore.setState({ isDisableEmergencyStop: true })
    this.reelNodes.forEach((reel, reelIndex) => {
      const isAnimationEnd = reel.children.some(symbol => symbol.y === 660)
      if (!isAnimationEnd) {
        this.immediatelyStopRemainingReels.push(reelIndex)
      }
    })
    this.currentSpinTimeline?.progress(1)

    ga4Info.recordImmediateStop()
  }

  public async spin() {
    const allReelPromise: unknown[] = []
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
    this.reelNodes.forEach((reel, reelIndex) => {
      const symbols = reel.children

      symbols.forEach((symbol, symbolIdx) => {
        const symbolSpinPromise = new Promise<void>((resolve) => {
          const symbolIndex = (660 - symbol.y) / 165
          const symbolTimeline = gsap.timeline({
            onComplete: () => {
              if (symbolIdx === 0) {
                playStopSound()
              }

              resolve()
            },
          })

          symbolTimeline.to(symbol, {
            duration: isThisSpinInSpeedMode ? gameConfig.speedModeBounceDuration : (symbolIndex + 1) * gameConfig.bounceDuration,
            delay: isThisSpinInSpeedMode ? symbolIndex * gameConfig.speedModeSymbolDelay : (reelIndex * gameConfig.reelDelay) + (symbolIndex * gameConfig.symbolDelay),
            ease: 'power1.inOut',
            y: `825`,
          })

          symbolTimeline.play()
        })

        allReelPromise.push(symbolSpinPromise)
      })
    })
    await Promise.all(allReelPromise)

    this.reelNodes.forEach((reel) => {
      this.spinReel(reel)
    })
  }

  public openPlaceOrderLoading() {
    this.showLoagingId = setTimeout(() => {
      useGameStore.setState({ isShowPlaceOrderLoadingIcon: true })
    }, useGameStore.getState().isThisSpinInSpeedMode ? gameConfig.speedModeShowPlaceOrderDelayLoadingMs : gameConfig.showPlaceOrderDelayLoadingMs)
  }

  public closePlaceOrderLoading() {
    clearTimeout(this.showLoagingId)
    useGameStore.setState({ isShowPlaceOrderLoadingIcon: false })
  }

  public async dropNormalGameResult() {
    const allReelPromise: unknown[] = []
    const dropTable = useGameStore.getState().currentNormalTable
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
    const isDisableEmergencyStop = useGameStore.getState().isDisableEmergencyStop

    this.currentSpinTimeline = gsap.timeline()
    this.reelNodes.forEach((reel, reelIndex) => {
      const reelLength = reel.children.length - 1
      reel.children.forEach((symbol, symbolIndex) => {
        const symbolSpinPromise = new Promise<void>((resolve) => {
          const tileData = dropTable[reelIndex][symbolIndex] as SlotSymbolInfo
          // ...change symbol
          this.onChangeNode(symbol as Container, tileData)

          // ...drop symbol
          const slideTask = gsap.timeline({
            onComplete: () => {
              if (symbolIndex === 1) {
                playStopSound()
              }
              if (!isDisableEmergencyStop && this.immediatelyStopRemainingReels.indexOf(reelIndex) !== -1) {
                gsap.timeline({ onComplete: () => {
                  this.handleScatterIn(symbol, reelIndex, resolve, slideTask)
                } })
                  .to(symbol, { duration: 0.1, y: '+=30' })
                  .to(symbol, { duration: 0.2, y: '-=30' })
                  .play()
              }
              else {
                this.handleScatterIn(symbol, reelIndex, resolve, slideTask)
              }
            },
          })
          slideTask.to(symbol, {
            duration: isThisSpinInSpeedMode ? gameConfig.speedModeSpinDuration : gameConfig.spinDuration,
            delay: isThisSpinInSpeedMode ? (symbolIndex * gameConfig.speedModeSymbolDelay) : (reelIndex * gameConfig.reelDelay) + (symbolIndex * gameConfig.symbolDelay),
            y: `${gameConfig.nodeHeight * (reelLength - symbolIndex)}`,
          })

          if (!isDisableEmergencyStop) {
            setSymbolJellyAni(slideTask, symbol)
          }

          this.currentSpinTimeline?.add(slideTask, 0)
        })

        allReelPromise.push(symbolSpinPromise)
      })
    })
    await Promise.all(allReelPromise)
    this.currentSpinTimeline = null
    this.immediatelyStopRemainingReels = []
  }

  private async handleScatterIn(
    symbol: DisplayObject,
    reelIndex: number,
    resolve: () => void,
    slideTask: gsap.core.Timeline,
  ) {
    let spinPromise: Promise<void> = Promise.resolve() // 預設為已完成的 Promise

    if (Number(symbol.name) === SymbolType.Bonus) {
      spinPromise = symbolUtil.scatterIn({ node: symbol as Container, isSuper: false })
    }
    if (Number(symbol.name) === SymbolType.SuperBonus) {
      spinPromise = symbolUtil.scatterIn({ node: symbol as Container, isSuper: true })
    }

    await spinPromise

    resolve()
    if (reelIndex === this.reelNodes.length - 1) {
      slideTask.kill()
    }
  }

  private spinReel = (reel: Container) => {
    const symbols = reel.children.map(x => x as Container)

    symbols.forEach((symbol, symbolIndex) => {
      symbol.y = -gameConfig.nodeHeight * (symbolIndex + 1)
    })
  }

  private onChangeNode(node: Container, symbolInfo: SlotSymbolInfo): void {
    changeNode(node, symbolInfo)
  }
}

export const setSymbolJellyAni = (slideTask: gsap.core.Timeline, targetSymbol: DisplayObject) => {
  const symbolContainer = targetSymbol as Container
  const spriteContainer = symbolContainer.getChildAt(0) as Container
  const symbolSprite = spriteContainer.getChildAt(0)
  const baseSec = 0.015
  slideTask.to(symbolSprite.scale, {
    x: '+=0.1',
    y: '-=0.1',
    duration: 5 * baseSec,

  })
    .to(symbolSprite.position, {
      y: '+=10',
      duration: 5 * baseSec,
    }, '<')

    .to(symbolSprite.scale, {
      x: '-=0.125',
      y: '+=0.125',
      duration: 7 * baseSec,
    })
    .to(symbolSprite.position, {
      y: '-=18',
      duration: 7 * baseSec,
    }, '<')

    .to(symbolSprite.scale, {
      x: '+=0.025',
      y: '-=0.025',
      duration: 3.2 * baseSec,
    })
    .to(symbolSprite.position, {
      y: '+=8',
      duration: 3.2 * baseSec,
    }, '<')
}
